import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { Field, TextInput, TextArea, SelectInput, AdminModal, StatusPill } from "@/admin/adminUi";

const EMPTY = { title: "", department: "", location: "", employment_type: "Full-time", description: "", responsibilities: [], requirements: [], status: "open" };

export default function AdminCareers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/careers").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...editing,
        responsibilities: (editing._resp ?? (editing.responsibilities || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
        requirements: (editing._req ?? (editing.requirements || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
      };
      delete body._resp; delete body._req;
      if (editing.id) await api.put(`/admin/careers/${editing.id}`, body);
      else await api.post("/admin/careers", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this role?")) return; await api.delete(`/admin/careers/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="display text-3xl font-light text-oraya-mist">Careers</h1><p className="text-oraya-slate mt-1">Open positions at ORAYA.</p></div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-career" className="inline-flex items-center gap-2 rounded-lg bg-oraya-mist text-oraya-black px-4 py-2.5 text-sm font-medium hover:bg-white"><Plus className="w-4 h-4" /> New role</button>
      </div>

      <div className="bg-oraya-graphite border border-white/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-oraya-slate border-b border-white/[0.06]">
            <th className="px-6 py-4 font-medium">Title</th><th className="px-6 py-4 font-medium hidden md:table-cell">Department</th>
            <th className="px-6 py-4 font-medium">Status</th><th className="px-6 py-4 font-medium text-right">Actions</th></tr></thead>
          <tbody>
            {items.map((j) => (
              <tr key={j.id} className="border-b border-white/[0.04] text-oraya-mist" data-testid={`career-row-${j.id}`}>
                <td className="px-6 py-4"><div className="font-medium">{j.title}</div><div className="text-oraya-slate text-xs">{j.location} · {j.employment_type}</div></td>
                <td className="px-6 py-4 hidden md:table-cell text-oraya-slate">{j.department}</td>
                <td className="px-6 py-4"><StatusPill status={j.status} /></td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setEditing({ ...j, _resp: (j.responsibilities || []).join("\n"), _req: (j.requirements || []).join("\n") })} data-testid={`edit-career-${j.id}`} className="text-oraya-slate hover:text-oraya-mist p-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(j.id)} data-testid={`delete-career-${j.id}`} className="text-oraya-slate hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="4" className="px-6 py-10 text-center text-oraya-slate">No roles yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <AdminModal title={editing.id ? "Edit role" : "New role"} onClose={() => setEditing(null)} onSave={save} saving={saving} testid="career-modal">
          <Field label="Title"><TextInput value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="career-title" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Department"><TextInput value={editing.department} onChange={(v) => setEditing({ ...editing, department: v })} testid="career-department" /></Field>
            <Field label="Location"><TextInput value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="career-location" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Employment type"><TextInput value={editing.employment_type} onChange={(v) => setEditing({ ...editing, employment_type: v })} testid="career-type" /></Field>
            <Field label="Status"><SelectInput value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} testid="career-status" options={[{ value: "open", label: "Open" }, { value: "closed", label: "Closed" }]} /></Field>
          </div>
          <Field label="Description"><TextArea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="career-description" /></Field>
          <Field label="Responsibilities (one per line)"><TextArea value={editing._resp ?? (editing.responsibilities || []).join("\n")} onChange={(v) => setEditing({ ...editing, _resp: v })} testid="career-responsibilities" /></Field>
          <Field label="Requirements (one per line)"><TextArea value={editing._req ?? (editing.requirements || []).join("\n")} onChange={(v) => setEditing({ ...editing, _req: v })} testid="career-requirements" /></Field>
        </AdminModal>
      )}
    </div>
  );
}
